import {
  Cartesian3,
  HeadingPitchRoll,
  Quaternion,
  Transforms,
  Matrix4,
  Matrix3,
  JulianDate,
  defined,
} from "@cesium/engine";
import Utils from "../../basic/Utils";

/**
 * Property 값 가져오기
 * @private
 * @param {Property} property
 * @param {JulianDate} time
 */
const getPropertyValue = (property, time = new JulianDate()) => {
  let result;
  if (defined(property)) {
    if (defined(property.getValue)) {
      result = property.getValue(time);
    } else {
      result = property;
    }
  }

  return result;
};

/**
 * Cesium Entity & DitapEntity Editing Class
 * @alias EditEntity
 * @private
 */
class EditEntity {
  /**
   *
   * @param {Entity} entity
   * @returns {Matrix4}
   */
  static getMatrix(entity) {
    return entity.computeModelMatrix();
  }

  /**
   * @param {Entity} entity
   * @returns {Cartesian3}
   */
  static getBoundingSphere(entity) {
    return getPropertyValue(entity.position);
  }

  /**
   *
   * @param {Entity} entity
   * @returns {Cartesian3}
   */
  static getPosition(entity) {
    return getPropertyValue(entity.position);
  }

  /**
   *
   * @param {Entity} entity
   * @param {Cartesian3 | Property} position
   * @returns {Cartesian3 | Property}
   */
  static setPosition(entity, position) {
    entity.position = position;
    return position;
  }

  /**
   *
   * @param {Entity} entity
   * @param {boolean} enu
   * @returns {Object}
   */
  static decomposition(entity, enu = true) {
    const matrix4 = entity.computeModelMatrix(new JulianDate()); // 원본 행렬 B

    const position = entity.position.getValue(new JulianDate()); // 현재 중심점
    const originalPosition = position;

    let scale;
    let translation;
    let quaternion;
    let headingPitchRoll;
    if (enu) {
      const originalEnu = Transforms.eastNorthUpToFixedFrame(originalPosition); // 원본 중심정의 EastNorthUp 행렬 A
      const originalEnuInverse = Matrix4.inverse(originalEnu, new Matrix4()); // Inverse A^{-1}

      // A^{-1}BA => ENU 행렬 제거한 행렬 추출
      const matrix4_2 = Matrix4.multiply(
        originalEnuInverse,
        matrix4,
        new Matrix4()
      );

      // 회전행렬 추출
      const matrix3 = Matrix4.getRotation(matrix4_2, new Matrix3());

      scale = Matrix4.getScale(matrix4_2, new Cartesian3());
      translation = Matrix4.getTranslation(matrix4_2, new Cartesian3());
      quaternion = Quaternion.fromRotationMatrix(matrix3, new Quaternion());
      headingPitchRoll = HeadingPitchRoll.fromQuaternion(
        quaternion,
        new HeadingPitchRoll()
      );
    } else {
      // 회전행렬 추출
      const matrix3 = Matrix4.getRotation(matrix4, new Matrix3());

      scale = Matrix4.getScale(matrix4, new Cartesian3());
      translation = Matrix4.getTranslation(matrix4, new Cartesian3());
      quaternion = Quaternion.fromRotationMatrix(matrix3, new Quaternion());
      headingPitchRoll = HeadingPitchRoll.fromQuaternion(
        quaternion,
        new HeadingPitchRoll()
      );
    }

    const result = {
      position: position,
      originalPosition: originalPosition,
      translation: translation,
      quaternion: quaternion,
      headingPitchRoll: headingPitchRoll,
      scale: scale,
    };

    return result;
  }

  /**
   *
   * @param {Entity} entity
   * @param {boolean} enu
   * @returns {HeadingPitchRoll}
   */
  static getHeadingPitchRoll(entity, enu = true) {
    const { headingPitchRoll } = this.decomposition(entity, enu);

    return headingPitchRoll;
  }

  /**
   *
   * @param {Entity} entity
   * @param {boolean} enu
   * @returns {Quaternion}
   */
  static getQuaternion(entity, enu = true) {
    const { quaternion } = this.decomposition(entity, enu);

    return quaternion;
  }

  /**
   *
   * @param {Entity} entity
   * @param {Quaternion | HeadingPitchRoll | Property} rotation
   * @returns {Quaternion | HeadingPitchRoll | Property}
   */
  static setRotation(entity, rotation) {
    if (rotation instanceof HeadingPitchRoll) {
      entity.orientation = Quaternion.fromHeadingPitchRoll(
        rotation,
        new Quaternion()
      );
    } else {
      entity.orientation = rotation;
    }

    return rotation;
  }

  /**
   *
   * @param {Entity} entity
   * @param {Cartesian3 | Property} scale
   * @returns {Cartesian3 | Property}
   */
  static setScale(entity, scale) {
    entity.scale = scale;

    return scale;
  }

  /**
   *
   * @param {Entity} entity
   * @returns {Cartesian3 | Property}
   */
  static getScale(entity) {
    const scale = Utils.getPropertyValue(entity.scale) || Cartesian3.ONE;
    return scale;
  }
}

export default EditEntity;
